// directives/permission-directive.js

/**
 * 按钮权限控制指令
 * 使用方法：
 * 1. v-permission="'business:project:post'" - 无权限时移除元素
 * 2. v-permission.disable="'business:project:put'" - 无权限时禁用元素
 * 3. v-permission.hide="'business:project:delete'" - 无权限时隐藏元素
 * 4. v-permission="['business:project:post', 'business:project:put']" - 多个权限需全部满足
 * 5. v-permission.any="['business:project:post', 'business:project:put']" - 多个权限满足任意一个
 */

import store from '@/store'

// 检查是否有权限
export function checkPermission(value, modifiers) {
    if (!value) {
        return true
    }
    const buttonPermissions = store.getters['permission/getMenuButtons'] || []
    // console.log('权限集合', buttonPermissions)
    if (!buttonPermissions || !buttonPermissions.length) return false

    // 数组处理
    if (Array.isArray(value)) {
        if (modifiers.any) {
            // 任意一个权限满足
            return value.some(perm => buttonPermissions.includes(perm))
        } else {
            // 所有权限都需要满足
            return value.every(perm => buttonPermissions.includes(perm))
        }
    }
    // 字符串处理
    return buttonPermissions.includes(value)
}

const permissionDirective = {
    // 指令绑定时
    bind(el, binding, vnode) {
        const { value, modifiers } = binding
        const hasPermission = checkPermission(value, modifiers)

        // 缓存原始状态
        if (modifiers.disable) {
            el.__originalDisabled = el.disabled
        }

        // 处理无权限情况
        if (!hasPermission) {
            handleNoPermission(el, modifiers, vnode)
        }
    },

    // 组件更新时
    update(el, binding, vnode) {

        const { value, oldValue, modifiers } = binding

        // 权限值发生变化时重新检查
        if (value !== oldValue) {
            const hasPermission = checkPermission(value, modifiers)

            if (hasPermission) {
                restorePermission(el, modifiers, vnode)
            } else {
                handleNoPermission(el, modifiers, vnode)
            }
        }
    },

    // 组件卸载时清理
    unbind(el) {
        // 清理自定义属性
        delete el.__originalDisabled
        delete el.__originalDisplay
        delete el.__originalVisibility
        delete el.__permissionCommentNode
    }
}

// 处理无权限的情况
function handleNoPermission(el, modifiers, vnode) {
    if (modifiers.disable) {
        // 禁用模式
        el.disabled = true
        // el.setAttribute('disabled', 'true')
        el.classList.add('is-disabled')

        if (!el.classList.contains('permission-disabled')) {
            el.classList.add('permission-disabled')
        }
    } else if (modifiers.hide) {
        // 隐藏模式（保留DOM位置）
        el.__originalDisplay = el.style.display
        el.style.display = 'none'
    } else if (modifiers.invisible) {
        // 不可见模式（保留DOM位置）
        el.__originalVisibility = el.style.visibility
        el.style.visibility = 'hidden'
    } else if (modifiers.remove) {
        // 移除模式（默认）- 使用注释节点替换
        if (!el.__permissionCommentNode) {
            const comment = document.createComment('permission-removed')
            el.__permissionCommentNode = comment

            if (el.parentNode) {
                el.parentNode.insertBefore(comment, el)
                el.parentNode.removeChild(el)
            }
        }
    } else {
        // 默认隐藏模式
        el.__originalDisplay = el.style.display
        el.style.display = 'none'
        // 默认移除模式
        // if (el.parentNode) {
        //     el.parentNode.removeChild(el)
        // }
    }
}

// 恢复权限
function restorePermission(el, modifiers, vnode) {
    if (modifiers.disable) {
        // 恢复禁用状态
        if (el.__originalDisabled !== undefined) {
            el.disabled = el.__originalDisabled
            if (!el.__originalDisabled) {
                el.removeAttribute('disabled')
            }
        } else {
            el.disabled = false
            el.removeAttribute('disabled')
        }
        el.classList.remove('permission-disabled')
    } else if (modifiers.hide) {
        // 恢复显示
        el.style.display = el.__originalDisplay || ''
    } else if (modifiers.invisible) {
        // 恢复可见性
        el.style.visibility = el.__originalVisibility || ''
    } else if (modifiers.remove) {
        // 恢复被注释替换的元素
        if (el.__permissionCommentNode && el.__permissionCommentNode.parentNode) {
            el.__permissionCommentNode.parentNode.insertBefore(el, el.__permissionCommentNode)
            el.__permissionCommentNode.parentNode.removeChild(el.__permissionCommentNode)
            delete el.__permissionCommentNode
        }
    } else {
        // 默认移除模式无法自动恢复，需要手动处理
        console.warn('默认移除模式的元素无法自动恢复，请使用其他修饰符')
    }
}

// 添加文档说明（可选）
permissionDirective.__description = '权限控制指令，用于控制元素基于权限的显示、隐藏、禁用或移除'
permissionDirective.__usage = `
  使用示例：
  1. 移除无权限元素：v-permission="'business:project:post'"
  2. 禁用无权限元素：v-permission.disable="'business:project:put'"
  3. 隐藏无权限元素：v-permission.hide="'business:project:delete'"
  4. 不可见无权限元素：v-permission.invisible="'business:project:get'"
  5. 多个权限全部满足：v-permission="['perm1', 'perm2']"
  6. 多个权限任意满足：v-permission.any="['perm1', 'perm2']"
`

export default permissionDirective